// Album API — typed wrappers for /api/v1/albums/* + /api/v1/media/* (W3 §5).
//
// The album module owns the 3-tier Media → AlbumItem → Album model.  Both
// diary photo uploads and album-specific uploads go through POST /api/v1/media
// (multipart, album_id? + caption?).  Read endpoints expose the album's
// cover_media + item_count alongside the basic metadata.

import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import type {
  AlbumCreate as SchemaAlbumCreate,
  AlbumItemUpdate as SchemaAlbumItemUpdate,
  AlbumUpdate as SchemaAlbumUpdate,
  UserRef as SchemaUserRef,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

/**
 * Build an `ApiError` whose message is something a user can read.
 *
 * W39.53 — every non-2xx branch in this file used to pass
 * `res.response.statusText` **positionally** into `ApiError`, which the copy
 * guard could not see (it only matched the `??` form and a literal in the
 * message slot). `call()` does not throw, so the branch was live: a 401 after a
 * revoked session, or a 502 during a deploy, rendered "Unauthorized" /
 * "Bad Gateway" as the album screen's error copy — `Albums.tsx` shows
 * `error.message` verbatim.
 *
 * `uploadMedia` in this same file already read `error.message` by hand; this
 * helper is that logic, so the ten positional call sites and the one
 * hand-rolled case now agree.
 */
async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.81 — `res.error` first. `openapi-fetch` parses the body itself
  // (`dist/index.js:244`) and hands the parsed value back as `result.error`, so
  // the `Response` in `res.response` arrives with its body already consumed and
  // `clone().json()` cannot read it. The effect of the old body was that **every**
  // album error answered `ALBUM_*_FAILED` / `API_REQUEST_FAILED` no matter what the
  // server said, and every `err.code`-dispatched copy mapper downstream was dead.
  //
  // W39.79 fixed this in `pair.ts` and `dashboard.ts` and described the result as
  // repo-wide. It was not: five modules each carry a **private** copy of this
  // function, and the claim was made from a grep for `clone().json()` whose
  // reading I did not finish. The `call()` in each of them was already carrying
  // `error` — nothing was reading it. A fix named after the shape of the problem
  // rather than its extent is the same mistake as a fix named after the shape of
  // the code.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null
  )?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

// ----- Schemas (mirror backend app/modules/album/schemas.py) ---------------

export interface MediaRef {
  id: string;
  url: string;
  mime_type: string;
  width: number | null;
  height: number | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type UserRef = SchemaUserRef;

export interface AlbumRead {
  id: string;
  pair_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  cover_media: MediaRef | null;
  /**
   * W39.54 — whether `cover_media` was chosen or is derived from the newest
   * photo. The server recomputes an *automatic* cover on every upload and
   * delete, and will not touch a chosen one; the UI needs this bit to say which
   * state it is in and to offer the way back.
   */
  cover_is_manual: boolean;
  item_count: number;
  created_by: UserRef;
  created_at: string;
  updated_at: string;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type AlbumCreateInput = SchemaAlbumCreate;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type AlbumUpdateInput = SchemaAlbumUpdate;

/**
 * 刻意手寫：contract 的 `AlbumRead` 把 `description` / `cover_media` 標成
 * optional，而每個 album 畫面都直接讀它們。alias 外層會讓這些讀取多出
 * `| undefined`，卻掛在一個承諾「完整 `AlbumRead`」的名字底下。
 * 見 `schema-parity.test.ts`。
 */
export interface AlbumListResponse {
  albums: AlbumRead[];
}

// --- Trash (W39.61) ---------------------------------------------------------
//
// A soft-deleted album is *findable* and restorable for 30 days. Before this,
// `DELETE /albums/{id}` was the only way to remove one: the album left the list,
// its detail 404'd, no route brought it back and no sweep collected it — while
// the mobile UX sweep taps 「移到垃圾桶」 on that very grid.
//
// `daysLeft` and `canRestore` come from the server so the 「N 天後永久刪除」 line,
// the 還原 button's enabled state and the cron that deletes the row cannot drift.

export interface AlbumTrashItem {
  id: string;
  name: string;
  deleted_at: string;
  /** Whole days until the retention job hard-deletes this. 0 means due now. */
  daysLeft: number;
  canRestore: boolean;
}

interface AlbumTrashResponse {
  items: Array<{
    id: string;
    name: string;
    deleted_at: string;
    days_left: number;
    can_restore: boolean;
  }>;
}

export async function listAlbumTrash(): Promise<AlbumTrashItem[]> {
  const res = await call<AlbumTrashResponse>("GET", "/api/v1/albums/trash");
  if (!res.response.ok) throw await apiError(res, "ALBUM_TRASH_FAILED");
  return (res.data?.items ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    deleted_at: r.deleted_at,
    daysLeft: r.days_left,
    canRestore: r.can_restore,
  }));
}

export async function restoreAlbum(albumId: string): Promise<AlbumRead> {
  const res = await call<AlbumRead>("POST", `/api/v1/albums/${albumId}/restore`);
  if (!res.response.ok) throw await apiError(res, "ALBUM_RESTORE_FAILED");
  return res.data as AlbumRead;
}

export async function purgeAlbum(albumId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/albums/${albumId}/permanent`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "ALBUM_PURGE_FAILED");
}

export interface AlbumItemRead {
  id: string;
  album_id: string;
  media: MediaRef;
  caption: string | null;
  taken_at: string | null;
  uploaded_by: UserRef;
  position: number;
  created_at: string;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type AlbumItemUpdateInput = SchemaAlbumItemUpdate;

export interface AlbumItemListResponse {
  items: AlbumItemRead[];
  total: number;
}

export interface MediaRead {
  id: string;
  file_path: string;
  url: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  uploaded_by: UserRef;
  uploaded_at: string;
}

export interface MediaUploadResponse {
  items: AlbumItemRead[];
}

// ----- Albums --------------------------------------------------------------

/** GET /api/v1/albums — pair albums (system 「未分類」 first). */
export async function listAlbums(): Promise<AlbumRead[]> {
  const res = await call<AlbumListResponse>("GET", "/api/v1/albums");
  if (!res.response.ok) throw await apiError(res, "ALBUM_LIST_FAILED");
  return res.data?.albums ?? [];
}

/** POST /api/v1/albums — create a custom album. */
export async function createAlbum(payload: AlbumCreateInput): Promise<AlbumRead> {
  const res = await call<AlbumRead>("POST", "/api/v1/albums", { body: payload });
  if (!res.response.ok) throw await apiError(res, "ALBUM_CREATE_FAILED");
  return res.data as AlbumRead;
}

/** GET /api/v1/albums/{id} — album detail + item_count + cover_media. */
export async function getAlbum(albumId: string): Promise<AlbumRead> {
  const res = await call<AlbumRead>("GET", `/api/v1/albums/${albumId}`);
  if (!res.response.ok) throw await apiError(res, "ALBUM_GET_FAILED");
  return res.data as AlbumRead;
}

/** PATCH /api/v1/albums/{id} — rename / update description. */
export async function updateAlbum(albumId: string, payload: AlbumUpdateInput): Promise<AlbumRead> {
  const res = await call<AlbumRead>("PATCH", `/api/v1/albums/${albumId}`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "ALBUM_UPDATE_FAILED");
  return res.data as AlbumRead;
}

/** DELETE /api/v1/albums/{id} — soft-delete (system album blocked with 422). */
export async function deleteAlbum(albumId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/albums/${albumId}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "ALBUM_DELETE_FAILED");
}

// ----- Album items ---------------------------------------------------------

export type AlbumItemSort = "taken_at_desc" | "taken_at_asc" | "position";

/** GET /api/v1/albums/{id}/items — paginated items. */
export async function listAlbumItems(
  albumId: string,
  opts: { limit?: number; offset?: number; sort?: AlbumItemSort } = {},
): Promise<AlbumItemListResponse> {
  const params: Record<string, unknown> = {
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
    sort: opts.sort ?? "taken_at_desc",
  };
  const res = await call<AlbumItemListResponse>("GET", `/api/v1/albums/${albumId}/items`, {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "ALBUM_ITEMS_FAILED");
  return res.data ?? { items: [], total: 0 };
}

/** PATCH /api/v1/albums/{id}/items/{itemId} — update caption / position. */
export async function updateAlbumItem(
  albumId: string,
  itemId: string,
  payload: AlbumItemUpdateInput,
): Promise<AlbumItemRead> {
  const res = await call<AlbumItemRead>("PATCH", `/api/v1/albums/${albumId}/items/${itemId}`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "ALBUM_ITEM_UPDATE_FAILED");
  return res.data as AlbumItemRead;
}

/** DELETE /api/v1/albums/{id}/items/{itemId} — remove from album. */
export async function deleteAlbumItem(albumId: string, itemId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/albums/${albumId}/items/${itemId}`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "ALBUM_ITEM_DELETE_FAILED");
}

// ----- Media ---------------------------------------------------------------

export interface UploadMediaOptions {
  /** Target album id; omit to send to system 「未分類」. */
  albumId?: string | null;
  /** Caption applied to each new AlbumItem. */
  caption?: string | null;
  /** Per-file MIME whitelist (default: image/jpeg, image/png, image/webp,
   *  image/heic). Backend additionally accepts video/mp4 + video/quicktime. */
  accept?: string[];
  /** Per-file byte cap (default 50MB per W3 §5.2). */
  maxBytes?: number;
}

/**
 * POST /api/v1/media — multipart upload.  Accepts multiple files + optional
 * ``albumId`` + ``caption``.  Returns one ``AlbumItemRead`` per uploaded file.
 */
export async function uploadMedia(
  files: File[],
  opts: UploadMediaOptions = {},
): Promise<AlbumItemRead[]> {
  if (files.length === 0) return [];
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  if (opts.albumId) form.append("album_id", opts.albumId);
  if (opts.caption) form.append("caption", opts.caption);

  // openapi-fetch forbids setting Content-Type with FormData — it computes
  // the multipart boundary itself.
  const reqOpts = {
    body: form,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headers: { "Content-Type": undefined as any },
  };
  const res = await call<MediaUploadResponse>("POST", "/api/v1/media", reqOpts);
  // This site already read `error.message` by hand, but seeded `message` with
  // `statusText`, so a non-JSON body (a 502 from the reverse proxy during a
  // deploy) still put "Bad Gateway" in front of a user. The helper is that
  // logic with the fallback the contract actually specifies.
  if (!res.response.ok) throw await apiError(res, "MEDIA_UPLOAD_FAILED");
  return res.data?.items ?? [];
}

/** GET /api/v1/media/{id} — metadata + url. */
export async function getMedia(mediaId: string): Promise<MediaRead> {
  const res = await call<MediaRead>("GET", `/api/v1/media/${mediaId}`);
  if (!res.response.ok) throw await apiError(res, "MEDIA_GET_FAILED");
  return res.data as MediaRead;
}

/** DELETE /api/v1/media/{id} — owner-only soft-delete + cascade AlbumItems. */
export async function deleteMedia(mediaId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/media/${mediaId}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "MEDIA_DELETE_FAILED");
}

/** URL the ``<img src>`` uses — backend streams the bytes from
 * ``GET /api/v1/media/{id}/file``. */
export function mediaFileUrl(mediaId: string): string {
  return `/api/v1/media/${mediaId}/file`;
}
